import dayjs from "utils/dayjs";
import richTextResolver, { ResolvedRichText } from "../richTextResolver";
import { DEFAULT_DEPOSIT_PERCENTAGE_DECIMAL } from "lib/constants";

import type { PDPPromo } from "services/cms/models/pdpPromo";
import type { PDPPromoCriteria } from "services/cms/models/pdpPromoCriteria";
import type { PDPPromosRow } from "services/cms/models/pdpPromosRow";
import type { VehicleService } from "services/vehicle/vehicle";
import { ResponseVehicle } from "services/motorpoint/shortlist";
import getAllSites from "services/cms/getAllSites";
import siteResolver from "../siteResolver";

type HEXColor = `#${string}`;

export type ResolvedPDPPromo = {
    body: ResolvedRichText;
    bgColor: HEXColor;
    textColor: HEXColor;
};

type Condition = "greater_than" | "less_than" | "equal_to" | "not_equal_to";

const compareValue = (condition: Condition, valueA: any, valueB: any) => {
    if (typeof valueA === "boolean") {
        return condition === "equal_to" ? valueA === valueB : false;
    }

    if (typeof valueA === "number" && ["greater_than", "less_than"].includes(condition)) {
        switch (condition) {
            case "greater_than":
                return valueA > +valueB;
            case "less_than":
                return valueA < +valueB;
            default:
                return false;
        }
    }

    if (["equal_to", "not_equal_to"].includes(condition)) {
        const valueBArr = valueB.split(",").map((val: string) => val.toLowerCase().trim());

        switch (condition) {
            case "equal_to":
                return valueBArr.includes(valueA.toString().toLowerCase());
            case "not_equal_to":
                return !valueBArr.includes(valueA.toString().toLowerCase());
        }
    }
};

export const findPromo = (
    vehicle: VehicleService.Vehicle,
    promos: PDPPromo[],
    ignorePromoState: boolean = false
) => {
    const promo = promos.find((promo: PDPPromo) => {
        const criteria = promo.elements.criteria.linkedItems;
        const state = promo.elements.state.value[0].codename as unknown as string;
        const startDate = promo.elements.startDate.value;
        const endDate = promo.elements.endDate.value;

        if (
            (!ignorePromoState && state === "off") ||
            (startDate && dayjs().isBefore(new Date(startDate))) ||
            (endDate && dayjs().isAfter(new Date(endDate)))
        ) {
            return false;
        }

        return criteria.every((c: PDPPromoCriteria) => {
            const field = c.elements.field.value[0].codename as string;
            const condition = c.elements.condition.value[0].codename as unknown as Condition;
            const value = c.elements.value.value;

            switch (field) {
                case "make": {
                    return compareValue(condition, vehicle.Taxonomy.Manufacturer, value);
                }
                case "model": {
                    return compareValue(condition, vehicle.Taxonomy.Model, value);
                }
                case "variant": {
                    return compareValue(condition, vehicle.Taxonomy.Variant, value);
                }
                case "trim": {
                    return compareValue(condition, vehicle.Taxonomy.Trim, value);
                }
                case "colour": {
                    return compareValue(condition, vehicle.Colour.Name, value);
                }
                case "seats": {
                    const seats = vehicle.CAPTechnicalData["Vehicle Dimensions"]?.["No. of Seats"];

                    return compareValue(condition, seats, value);
                }
                case "body_type": {
                    return compareValue(condition, vehicle.BodyType, value);
                }
                case "fuel_type": {
                    return compareValue(condition, vehicle.FuelType, value);
                }
                case "transmission": {
                    return compareValue(condition, vehicle.Transmission, value);
                }
                case "branch": {
                    return compareValue(condition, vehicle.AllocatedLocation.Id, value);
                }
                case "drive_train": {
                    return compareValue(condition, vehicle.DriveTrain, value);
                }
                case "number_of_doors": {
                    return compareValue(condition, vehicle.DoorQuantity, value);
                }
                // case "engine_size": {
                //  const engineAndDriveTrain = vehicle.CAPTechnicalData["Engine and Drive Train"];
                //  return compareValue(condition, vehicle.DoorQuantity, value);
                // }
                case "mileage": {
                    return compareValue(condition, vehicle.Mileage, value);
                }
                case "price": {
                    const vehiclePrice =
                        vehicle.CurrentPrice.SalePrice.BasicPrice +
                        (vehicle?.CurrentPrice?.SalePrice?.VATAmount ?? 0);
                    return compareValue(condition, vehiclePrice, value);
                }
                case "age": {
                    const carAge =
                        new Date().getFullYear() -
                        new Date(vehicle.DateOfFirstUKRegistration).getFullYear();

                    return compareValue(condition, carAge, value);
                }
                case "monthly_payment": {
                    const monthlyPayment =
                        (vehicle.CurrentPrice.SalePrice.BasicPrice +
                            (vehicle.CurrentPrice?.SalePrice?.VATAmount ?? 0)) *
                        DEFAULT_DEPOSIT_PERCENTAGE_DECIMAL;

                    return compareValue(condition, monthlyPayment, value);
                }
                case "emissions": {
                    const emissions = vehicle.CAPTechnicalData["Emissions - ICE"]?.["CO2 (g/km)"];
                    return compareValue(condition, emissions, value);
                }
                case "horsepower": {
                    const bhp = vehicle.CAPTechnicalData.Performance?.["Engine Power - BHP"];
                    return compareValue(condition, bhp, value);
                }
                case "is_commercial": {
                    return compareValue(condition, vehicle.Taxonomy.IsCommercial, value === "yes");
                }
                case "stock_number": {
                    return compareValue(condition, vehicle.StockBookNumber, value);
                }
                default:
                    return false;
            }
        });
    });

    const fallbackPromo =
        promos?.findLast &&
        promos?.findLast((promo: PDPPromo) => {
            const criteria = promo.elements.criteria.linkedItems;
            return !criteria.length;
        });

    return promo ?? fallbackPromo ?? undefined;
};

export const findPromoForResponseVehicle = (
    responseVehicle: ResponseVehicle,
    promos: PDPPromo[],
    ignorePromoState: boolean = false
) => {
    const promo = promos.find((promo: PDPPromo) => {
        const criteria = promo.elements.criteria.linkedItems;
        const state = promo.elements.state.value[0].codename as unknown as string;
        const startDate = promo.elements.startDate.value;
        const endDate = promo.elements.endDate.value;

        if (
            (!ignorePromoState && state === "off") ||
            (startDate && dayjs().isBefore(new Date(startDate))) ||
            (endDate && dayjs().isAfter(new Date(endDate)))
        ) {
            return false;
        }

        return criteria.every((c: PDPPromoCriteria) => {
            const field = c.elements.field.value[0].codename as string;
            const condition = c.elements.condition.value[0].codename as unknown as Condition;
            const value = c.elements.value.value;

            switch (field) {
                case "make": {
                    return compareValue(condition, responseVehicle.Make, value);
                }
                case "model": {
                    return compareValue(condition, responseVehicle.Model, value);
                }
                case "variant": {
                    return compareValue(condition, responseVehicle.Range, value);
                }
                case "trim": {
                    return compareValue(condition, responseVehicle.Trim, value);
                }
                case "colour": {
                    return compareValue(condition, responseVehicle.Colour, value);
                }
                // case "seats": {
                //     const seats = responseVehicle.CAPTechnicalData["Vehicle Dimensions"]?.["No. of Seats"];

                //     return compareValue(condition, seats, value);
                // }
                case "body_type": {
                    return compareValue(condition, responseVehicle.BodyStyle, value);
                }
                case "fuel_type": {
                    return compareValue(condition, responseVehicle.FuelType, value);
                }
                case "transmission": {
                    return compareValue(condition, responseVehicle.Transmission, value);
                }
                case "branch": {
                    return compareValue(condition, responseVehicle.LocationId, value);
                }
                // case "drive_train": {
                //     return compareValue(condition, responseVehicle.DriveTrain, value);
                // }
                // case "number_of_doors": {
                //     return compareValue(condition, responseVehicle.DoorQuantity, value);
                // }
                case "mileage": {
                    return compareValue(condition, responseVehicle.Mileage, value);
                }
                case "price": {
                    const vehiclePrice =
                        responseVehicle.CurrentPrice ?? 0 + (responseVehicle.VatAmount ?? 0);
                    return compareValue(condition, vehiclePrice, value);
                }
                case "age": {
                    const carAge = new Date().getFullYear() - (responseVehicle.RegYear ?? 0);

                    return compareValue(condition, carAge, value);
                }
                case "monthly_payment": {
                    const monthlyPayment =
                        (responseVehicle.CurrentPrice ?? 0 + (responseVehicle.VatAmount ?? 0)) *
                        DEFAULT_DEPOSIT_PERCENTAGE_DECIMAL;

                    return compareValue(condition, monthlyPayment, value);
                }
                // case "emissions": {
                //     const emissions = responseVehicle.CAPTechnicalData["Emissions - ICE"]?.["CO2 (g/km)"];
                //     return compareValue(condition, emissions, value);
                // }
                // case "horsepower": {
                //     const bhp = responseVehicle.CAPTechnicalData.Performance?.["Engine Power - BHP"];
                //     return compareValue(condition, bhp, value);
                // }
                case "is_commercial": {
                    return compareValue(condition, responseVehicle.IsCommercial, value === "yes");
                }
                case "stock_number": {
                    return compareValue(condition, responseVehicle.StockBookNumber, value);
                }
                default:
                    return false;
            }
        });
    });

    const fallbackPromo =
        promos?.findLast &&
        promos?.findLast((promo: PDPPromo) => {
            const criteria = promo.elements.criteria.linkedItems;
            return !criteria.length;
        });

    return promo ?? fallbackPromo ?? undefined;
};

const pdpPromoRowResolver = (
    vehicle: VehicleService.Vehicle,
    promoRow: PDPPromosRow
): ResolvedPDPPromo | null => {
    const { promos } = promoRow.elements;

    const promo = findPromo(vehicle, promos.linkedItems);

    if (promo) {
        const { body, backgroundColour, textColour } = promo.elements;

        return {
            body: richTextResolver({
                html: body.value,
                linkedItems: body.linkedItems,
            }),
            bgColor: backgroundColour?.value as HEXColor,
            textColor: textColour?.value as HEXColor,
        };
    }

    return null;
};

export default pdpPromoRowResolver;
